import React, { useRef, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import toastService from '../../services/toastService';
import { useDropzone } from 'react-dropzone';
import { useAuth } from '../../context/AuthContext';
import { uploadFile } from '../../services/fileUploadService';
import { createChatForKb, createMessageForChat, searchKnowledgeBase, ingestFileToKb, getAllMessagesByFilter, executePipelineSessionAgent } from '../../services/kbQueryService';
import { X, Send, Bot, User as UserIcon } from 'lucide-react';
import {
  FloatingCard,
  ChatInput,
  ChatButtons,
  ChatWrapper,
  ChatScrollContainer,
  ChatBottom,
  FormRowFull,
  Button,
  ChatBotScrollContainer,
  Card,
  MarkdownContainer
} from '../../styles/PageStyles';

const ChatBot = ({ knowledgeBaseCode, conversationId, context, executionResults, pipelineSummaryInfo, onClose, isOpen, embedded = false }) => {
  const { user } = useAuth();
  const [messageList, setMessageList] = useState([]);
  const [transcribedText, setTranscribedText] = useState('');
  const [queryContext, setQueryContext] = useState('query');
  const [isTyping, setIsTyping] = useState(false);
  const [uploadedFileDetails, setUploadedFileDetails] = useState([]);
  const chatContainerRef = useRef(null);
  const [maxFiles, setMaxFiles] = useState(10);
  const [maxSize, setMaxSize] = useState(10 * 1024 * 1024); // 10 MB
  const [fileTypes, setFileTypes] = useState({
    'application/pdf': ['.pdf'],
    'image/png': ['.png'],
    'image/jpeg': ['.jpg', '.jpeg']
  });

  // Define handleUpload with useCallback to prevent recreating on each render
  const handleUpload = React.useCallback(async (file) => {
    if (file) {
      try {
        const response = await uploadFile(file, knowledgeBaseCode, 'adhoc', new Date().getTime());
        return response;
      } catch (error) {
        console.error('Error uploading file:', error);
        toastService.error(`Error uploading file: ${error.message}`);
      }
    }
  }, [knowledgeBaseCode]);

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messageList]); // Scroll whenever messageList updates
  
  // Load previous messages when chat is opened
  useEffect(() => {
    const loadPreviousMessages = async () => {
      // Here before loading the message list i want to add executionResults to the message list
      if (executionResults && executionResults.length > 0) {
        // Convert each execution result into a chat message format using its summary field
        console.log("Adding execution results to chat:", executionResults); 
        const formattedResults = executionResults.map(result => ({
          kb: knowledgeBaseCode,
          to: user?.username,
          code: "MSG" + new Date().getTime() + "-" + (result.id || Math.random().toString(36).substring(2, 8)),
          from: "agent",
          prevConvName: result.name || null,
          message: result.summary || "No summary available for this action.",
          company_cd: user?.companyCd,
          conversation: conversationId,
          created_at: result.created_at
        }));
        setMessageList(prevMessages => [...formattedResults, ...prevMessages]);
      }

      if (isOpen && knowledgeBaseCode && conversationId) {
        try {
          const filter = {
            kb: knowledgeBaseCode,
            conversation: conversationId
          };
          const response = await getAllMessagesByFilter(filter, { created_at: 'asc' });
          if (response && response.length > 0) {
            setMessageList(prevMessages => [...response, ...prevMessages]);
          }else{
            console.log("no previous messages found", context);
            // Create a JSON file with context data
            const contextData = {
              context: context,
              kb: knowledgeBaseCode,
              conversation: conversationId,
              timestamp: new Date(),
              user: user.username
            };
            const jsn = JSON.stringify(contextData);
            const blob = new Blob([jsn], { type: 'application/json' });
            const file = new File([blob], `temp${knowledgeBaseCode}_${conversationId}.json`);
            
            // Upload the file using handleUpload
            try {
              const uploadResponse = await handleUpload(file);
              if (uploadResponse && uploadResponse.data && uploadResponse.data.url) {
                console.log("Context file uploaded successfully:", uploadResponse.data.url);
                
                // Create a welcome message
                const welcomeMessage = {
                  kb: knowledgeBaseCode,
                  to: user.username,
                  code: "MSG" + new Date().getTime(),
                  from: "agent",
                  message: `Welcome to the chat! How can I assist you today?`,
                  company_cd: user.companyCd,
                  conversation: conversationId,
                  created_at: new Date(),
                  contextUrl: uploadResponse.data.url
                };
                
                // Save the welcome message
                await createMessageForChat(welcomeMessage);
                setMessageList([welcomeMessage]);
              } else {
                console.error("Failed to upload context file:", uploadResponse);
              }
            } catch (error) {
              console.error("Error uploading context file:", error);
            }
          }
        } catch (error) {
          console.error("Error loading previous messages:", error);
          toastService.error("Failed to load previous messages. Please try again.");
        }
      }
    };
    
    loadPreviousMessages();
  }, [isOpen, knowledgeBaseCode, conversationId, context, user.username, user.companyCd, handleUpload, executionResults]);

  // Function to handle file drop
  const onDrop = async (acceptedFiles, rejectedFiles) => {
    // Handle rejected files
    if (rejectedFiles.length > 0) {
      rejectedFiles.forEach((file) => {
        if (file.errors.some((err) => err.code === 'file-too-large')) {
          toastService.error(`File "${file.file.name}" exceeds the maximum size of ${maxSize / (1024 * 1024)} MB.`);
        }
        if (file.errors.some((err) => err.code === 'file-invalid-type')) {
          const allowedTypes = Object.keys(fileTypes).map(type => type.split('/')[1]).join(', ');
          toastService.error(`File "${file.file.name}" is not an allowed type. Allowed types: ${allowedTypes}`);
        }
      });
    }

    // No accepted files
    if (acceptedFiles.length === 0) {
      return;
    }

    // Check if knowledge base code is available
    if (!knowledgeBaseCode) {
      toastService.error("Knowledge base information is missing. Please reload the page.");
      return;
    }

    // Check if adding new files exceeds the maxFiles limit
    if (uploadedFileDetails.length + acceptedFiles.length > maxFiles) {
      toastService.error(`You can only upload up to ${maxFiles} files.`);
      return;
    }

    try {
      // Show loading toast
      toastService.info(`Uploading ${acceptedFiles.length} files...`);
      
      // Upload each file but don't ingest yet
      const newUploadedFiles = [];
      for (const file of acceptedFiles) {
        try {
          const uploadResponse = await handleUpload(file);
          if (uploadResponse && uploadResponse.data && uploadResponse.data.url) {
            newUploadedFiles.push({
              url: uploadResponse.data.url,
              name: file.name,
              size: file.size,
              type: file.type
            });
            toastService.success(`File "${file.name}" uploaded successfully`);
          } else {
            toastService.error(`Failed to upload file "${file.name}". Response format incorrect.`);
            console.error('Upload response format incorrect:', uploadResponse);
          }
        } catch (error) {
          toastService.error(`Failed to upload file "${file.name}": ${error.message || "Unknown error"}`);
          console.error(`Error uploading file ${file.name}:`, error);
        }
      }
      
      // Update the files state with detailed information
      if (newUploadedFiles.length > 0) {
        setUploadedFileDetails(prevFiles => [...prevFiles, ...newUploadedFiles]);
      } else {
        toastService.error("No files were successfully uploaded.");
      }
    } catch (error) {
      console.error('Error in file upload process:', error);
      toastService.error(`Error uploading files: ${error.message || "Unknown error"}`);
    }
  };

  // handleUpload is now defined above with useCallback
  
  // Function to remove a file from the uploaded files list
  const removeFile = (fileUrl) => {
    setUploadedFileDetails(prevFiles => prevFiles.filter(file => file.url !== fileUrl));
  };

  // Function to handle the ingestion process
  const handleIngestFiles = async () => {
    if (uploadedFileDetails.length === 0) {
      // If no files are uploaded, check if there's text before submitting
      if (transcribedText.trim()) {
        handleSubmitQuery();
      }
      return;
    }

    try {
      // Extract just the URLs for ingestion
      const fileUrls = uploadedFileDetails.map(file => file.url);
      if (!fileUrls.length || fileUrls.some(url => !url)) {
        toastService.error("Some file URLs are missing. Please try uploading again.");
        return;
      }
      
      // Get the file names for display in toast messages
      const fileNames = uploadedFileDetails.map(file => file.name).join(", ");
      toastService.info(`Processing files: ${fileNames}`);
      
      // Show loading toast
      toastService.info(`Starting ingestion of ${fileUrls.length} files...`);
      
      // Ingest files to knowledge base
      const ingestResponse = await ingestFileToKb(
        knowledgeBaseCode, 
        fileUrls, 
        {}, 
        true
      );
      
      if (ingestResponse) {
        toastService.success(`Files ingested successfully!`);
        
        // Clear the uploaded files after ingestion
        setUploadedFileDetails([]);
        
        // Also process any query text if it exists
        if (transcribedText.trim()) {
          handleSubmitQuery();
        }
      } else {
        toastService.error("Failed to ingest files. Please try again.");
      }
    } catch (error) {
      console.error('Error ingesting files:', error);
      toastService.error(`Error ingesting files: ${error.message || "Unknown error"}`);
    }
  };

  const handleVoiceRecording = () => {
    // Check if the browser supports SpeechRecognition
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      toastService.error('Your browser does not support voice recognition. Please use a supported browser like Chrome or Edge.');
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.interimResults = false; // Only return final results
    recognition.maxAlternatives = 1;

    // Start recording
    recognition.start();

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setTranscribedText(transcript);
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      switch (event.error) {
        case 'not-allowed':
          toastService.error('Microphone access is blocked. Please allow microphone access in your browser settings.');
          break;
        case 'network':
          toastService.error('Network error occurred. Please check your internet connection.');
          break;
        case 'service-not-allowed':
          toastService.error('Speech recognition service is not allowed. Please check your browser settings.');
          break;
        case 'aborted':
          toastService.error('Speech recognition was aborted. Please try again.');
          break;
        case 'audio-capture':
          toastService.error('No microphone was found. Please ensure your microphone is connected and try again.');
          break;
        default:
          toastService.error('An error occurred during voice recognition. Please try again.');
      }
    };

    recognition.onend = () => {
      console.log('Voice recording ended.');
    };
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxFiles,
    maxSize,
    accept: fileTypes,
  });

  const handleSubmitQuery = async () => {
    setIsTyping(true);
    
    // Use provided conversationId if available, otherwise create a new one
    const conversationCode = conversationId || "CH-" + new Date().getTime();
    
    // Add the currently typed message in the MessageList
    const userMessage = {
      kb: knowledgeBaseCode,
      to: "agent",
      code: "MSG" + new Date().getTime(),
      from: user.username,
      message: transcribedText,
      company_cd: user.companyCd,
      conversation: conversationCode,
      created_at: new Date(),
    };

    setMessageList(prevMessageList => [...(prevMessageList || []), userMessage]);
    const currentQuery = transcribedText;
    setTranscribedText("");

    // Only create a new chat if we don't have a conversationId
    if (!conversationId) {
      // Create a new chat for the knowledge base
      let chatData = {
        title: "Chat started from Claims Triage",
        company_cd: user.companyCd,
        context: null,
        document_list: [],
        kb: knowledgeBaseCode,
        coversations: [],
        code: conversationCode,
        ingestion_status: null,
        is_selected: false,
        created_at: new Date()
      };
      
      // Send message to chat service
      try {
        await createChatForKb(chatData);
      } catch (error) {
        console.error("Failed to create chat:", error);
      }
    }
    
    // Save the message
    try {
      await createMessageForChat(userMessage);
    } catch (error) {
      console.error("Failed to save message:", error);
    }

    // Scroll to the bottom of the chat container
    setTimeout(() => {
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
      }
    }, 100);

      // Decide which backend to call for the agent response
    try {
      let agentMessageText;

      if (pipelineSummaryInfo && pipelineSummaryInfo.userId && pipelineSummaryInfo.sessionId) {
        // Use pipeline session agent when summary IDs are available
        const agentResponse = await executePipelineSessionAgent({
          session_id: pipelineSummaryInfo.sessionId,
          user_id: pipelineSummaryInfo.userId,
          query: currentQuery
        });
        agentMessageText = typeof agentResponse === 'string' ? agentResponse : agentResponse?.response_content || JSON.stringify(agentResponse);
      } else {
        // Fallback to existing knowledge base search behavior
        const response = await searchKnowledgeBase(
          knowledgeBaseCode,
          currentQuery,
          "Doc Name",
          conversationCode,
          queryContext
        );
        agentMessageText = response;
      }

      // Add the response message to the MessageList
      const botResponse = {
        kb: knowledgeBaseCode,
        to: user.username,
        code: "MSG" + new Date().getTime(),
        from: "agent",
        message: agentMessageText,
        company_cd: user.companyCd,
        conversation: conversationCode,
        created_at: new Date(),
      };

      await createMessageForChat(botResponse);
      setMessageList(prevMessageList => [...prevMessageList, botResponse]);
      
      setTimeout(() => {
        if (chatContainerRef.current) {
          chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
      }, 100);
      
    } catch (error) {
      console.error("Error processing chat query:", error);

      // Add a simple error message into the chat
      const errorMessage = {
        kb: knowledgeBaseCode,
        to: user.username,
        code: "MSG" + new Date().getTime(),
        from: "agent",
        message: "Sorry, I encountered an error processing your request. Please try again later.",
        company_cd: user.companyCd,
        conversation: conversationCode,
        created_at: new Date(),
      };

      setMessageList(prevMessageList => [...prevMessageList, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const chatDateFormat = (dateValue) => {
    if (!dateValue) return 'N/A';
    
    // Create date object without any timezone processing first
    let inputDate = dateValue;
    if (typeof dateValue === 'string') {
      if (!dateValue.endsWith('Z') && !dateValue.includes('+')) {
        inputDate = dateValue + 'Z';
      }
    }
    
    let date = new Date(inputDate);
    
    const timezoneOffsetMs = date.getTimezoneOffset() * 60 * 1000;
    
    date = new Date(date.getTime() - timezoneOffsetMs);
    
    if (isNaN(date.getTime())) {
      console.error('Invalid date in chatDateFormat:', dateValue);
      return 'Invalid Date';
    }
    
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const compareDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    const timeOptions = {
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    };

    // If it's today, just show time
    if (compareDate.getTime() === today.getTime()) {
      return date.toLocaleTimeString(undefined, timeOptions);
    }

    // If it's a different day, show day and month with time
    const dayOptions = {
      day: '2-digit',
      month: 'short',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    };

    return date.toLocaleString(undefined, dayOptions);
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: embedded ? 0 : 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: embedded ? 0 : 20 }}
      style={{
        position: embedded ? 'relative' : 'fixed',
        bottom: embedded ? 'auto' : '80px',
        right: embedded ? 'auto' : '20px',
        width: embedded ? '100%' : '400px',
        height: embedded ? '100%' : '600px',
        borderRadius: embedded ? '0' : '12px',
        boxShadow: embedded ? 'none' : '0 10px 25px rgba(0,0,0,0.2)',
        backgroundColor: 'white',
        overflow: 'hidden',
        zIndex: embedded ? 'auto' : 9999,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Header - Only show when not embedded */}
      {!embedded && (
        <div style={{
          padding: '14px 16px',
          backgroundColor: 'var(--primary-500)',
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: 'none'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: 'var(--text-sm)',
            fontWeight: 'var(--font-semibold)'
          }}>
            <Bot size={18} />
            Meta Pilot
          </div>
          <button 
            onClick={onClose}
            style={{ 
              background: 'transparent',
              border: 'none',
              padding: '4px',
              color: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <X size={18} />
          </button>
        </div>
      )}

      {/* Chat Area */}
      <ChatWrapper style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '0px' }}>
        <ChatBotScrollContainer className="ChatScrollContainer" ref={chatContainerRef}>
          {messageList.length === 0 && (
            <div style={{ 
              textAlign: 'center',
              padding: '2rem 1rem',
              maxWidth: '360px',
              margin: 'auto'
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                margin: '0 auto 1rem',
                background: 'var(--primary-500)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white'
              }}>
                <Bot size={24} />
              </div>
              <h3 style={{
                fontSize: 'var(--text-base)',
                fontWeight: 'var(--font-semibold)',
                color: 'var(--neutral-800)',
                marginBottom: '0.5rem'
              }}>Start a Conversation</h3>
              <p style={{
                fontSize: 'var(--text-xs)',
                color: 'var(--neutral-500)'
              }}>Ask questions or upload files for assistance</p>
            </div>
          )}
          
          {messageList?.map((conv, index) => (
            <React.Fragment key={index}>
              {conv.from === 'agent' ? (
                <motion.div
                  initial={{ opacity: 0, y: 20, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ duration: 0.3, ease: 'easeOut' }}
                  style={{
                    marginBottom: '0.5rem',
                    maxWidth: '100%'
                  }}
                >
                  <div style={{
                    background: '#f5f5f5',
                    borderRadius: '8px',
                    border: '1px solid #e0e0e0',
                    padding: '0.625rem 0.875rem'
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '0.5rem',
                      gap: '0.75rem'
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        flex: 1
                      }}>
                        <div style={{
                          width: '20px',
                          height: '20px',
                          minWidth: '20px',
                          borderRadius: '4px',
                          background: 'var(--primary-500)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white'
                        }}>
                          <Bot size={12} />
                        </div>
                        <span style={{
                          fontSize: 'var(--text-xs)',
                          fontWeight: 'var(--font-semibold)',
                          color: 'var(--neutral-900)'
                        }}>{conv.prevConvName || "Agent"}</span>
                      </div>
                      <span style={{
                        fontSize: '10px',
                        color: 'var(--neutral-500)',
                        whiteSpace: 'nowrap',
                        flexShrink: 0
                      }}>{chatDateFormat(conv.created_at)}</span>
                    </div>
                    <div style={{
                      fontSize: '11px',
                      lineHeight: '1.4',
                      color: 'var(--neutral-800)'
                    }}>
                      <MarkdownContainer>
                        <div className="markdown-content">
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {conv.message}
                          </ReactMarkdown>
                        </div>
                      </MarkdownContainer>
                    </div>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0, y: 20, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ duration: 0.3, ease: 'easeOut' }}
                  style={{
                    display: 'flex',
                    gap: '0.5rem',
                    marginBottom: '0.5rem',
                    marginLeft: 'auto',
                    maxWidth: '95%',
                    flexDirection: 'row-reverse'
                  }}
                >
                  <div style={{
                    width: '28px',
                    height: '28px',
                    minWidth: '28px',
                    borderRadius: '4px',
                    background: 'var(--neutral-600)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '11px',
                    fontWeight: 'var(--font-semibold)',
                    color: 'white'
                  }}>
                    {user?.firstname?.[0]?.toUpperCase() || 'U'}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      background: 'white',
                      borderRadius: '8px',
                      border: '1px solid #e0e0e0',
                      padding: '0.625rem 0.875rem'
                    }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: '0.5rem',
                        flexDirection: 'row-reverse',
                        gap: '1rem'
                      }}>
                        <span style={{
                          fontSize: 'var(--text-xs)',
                          fontWeight: 'var(--font-semibold)',
                          color: 'var(--neutral-900)'
                        }}>You</span>
                        <span style={{
                          fontSize: '10px',
                          color: 'var(--neutral-500)',
                          whiteSpace: 'nowrap',
                          flexShrink: 0
                        }}>{chatDateFormat(conv.created_at)}</span>
                      </div>
                      <div style={{
                        fontSize: 'var(--text-sm)',
                        lineHeight: '1.4',
                        color: 'var(--neutral-800)'
                      }}>
                        <MarkdownContainer>
                          <div className="markdown-content">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                              {conv.message}
                            </ReactMarkdown>
                          </div>
                        </MarkdownContainer>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </React.Fragment>
          ))}

          {/* Typing Loader */}
          {isTyping && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              style={{
                marginBottom: '0.5rem',
                maxWidth: '100%'
              }}
            >
              <div style={{
                background: '#f5f5f5',
                borderRadius: '8px',
                border: '1px solid #e0e0e0',
                padding: '0.625rem 0.875rem'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  marginBottom: '0.5rem'
                }}>
                  <div style={{
                    width: '20px',
                    height: '20px',
                    minWidth: '20px',
                    borderRadius: '4px',
                    background: 'var(--primary-500)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white'
                  }}>
                    <Bot size={12} />
                  </div>
                  <div style={{
                    fontSize: '10px',
                    color: 'var(--neutral-500)'
                  }}>Agent is typing...</div>
                </div>
                <div className="typing-loader">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </motion.div>
          )}
        </ChatBotScrollContainer>

        {/* Input Area */}
        <ChatBottom>
          <div style={{ width: '100%' }}>
            {uploadedFileDetails.length > 0 && (
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '6px',
                marginBottom: '6px'
              }}>
                    {uploadedFileDetails.map((file, index) => (
                      <div key={index} style={{
                        background: 'linear-gradient(135deg, var(--primary-50), white)',
                        border: '1px solid var(--primary-200)',
                        borderRadius: '20px',
                        padding: '0.5rem 1rem',
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: 'var(--text-xs)',
                        color: 'var(--primary-600)',
                        transition: 'all 0.2s ease'
                      }}>
                        <span style={{ marginRight: '8px' }}>{file.name}</span>
                        <span 
                          onClick={() => removeFile(file.url)}
                          style={{ 
                            cursor: 'pointer',
                            width: '18px',
                            height: '18px',
                            borderRadius: '50%',
                            background: 'var(--primary-200)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            color: 'var(--primary-600)',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = 'var(--primary-500)';
                            e.target.style.color = 'white';
                            e.target.style.transform = 'scale(1.1)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = 'var(--primary-200)';
                            e.target.style.color = 'var(--primary-600)';
                            e.target.style.transform = 'scale(1)';
                          }}
                        >×</span>
                      </div>
                    ))}
                  </div>
                )}
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <ChatInput 
                type="text" 
                value={transcribedText || ''}
                onChange={(e) => setTranscribedText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    if (uploadedFileDetails.length > 0) {
                      handleIngestFiles();
                    } else if (transcribedText.trim()) {
                      handleSubmitQuery();
                    }
                  }
                }} 
                placeholder="Type a message..."
              />
              <ChatButtons 
                className={transcribedText.trim() || uploadedFileDetails.length > 0 ? 'isActive' : ''} 
                onClick={() => {
                  if (uploadedFileDetails.length > 0) {
                    handleIngestFiles();
                  } else if (transcribedText.trim()) {
                    handleSubmitQuery();
                  }
                }}>
                <Send size={16} />
              </ChatButtons>
            </div>
          </div>
        </ChatBottom>
      </ChatWrapper>
    </motion.div>
  );
};

export default ChatBot;
